import { useTranslation } from 'react-i18next'
import { Check, Pencil, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { haptic } from '@/shared/utils/haptics'
import type { MealSlot as MealSlotType } from './types'

interface MealSlotProps {
  slot: MealSlotType
  dayName: string
  userRole: 'Planner' | 'Member' | null
  isToday?: boolean
  onTap: () => void
  onEditServing?: () => void
  onRemove?: () => void
  onApprove?: () => void
  onDismiss?: () => void
}

export function MealSlot({ slot, dayName, userRole, isToday = false, onTap, onEditServing, onRemove, onApprove, onDismiss }: MealSlotProps) {
  const { t } = useTranslation('meal-plan')

  // Empty slot
  if (!slot.recipeId) {
    const isMember = userRole === 'Member'
    const label = isMember ? t('slot.suggestLabel') : t('slot.emptyLabel')
    const ariaLabel = isMember
      ? t('slot.suggestSlotAriaLabel', { day: dayName })
      : t('slot.emptySlotAriaLabel', { day: dayName })

    return (
      <button
        type="button"
        onClick={() => { haptic('light'); onTap() }}
        aria-label={ariaLabel}
        className={cn(
          'w-full min-h-[44px] flex items-center justify-center rounded-lg border-2 border-dashed border-line text-muted text-sm font-medium hover:border-accent hover:text-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent py-3 px-2',
          isToday && 'border-l-4 border-l-accent',
        )}
      >
        {label}
      </button>
    )
  }

  // Suggestion state
  if (slot.isSuggestion) {
    return (
      <div
        data-testid="suggestion-slot"
        className="w-full min-h-[44px] rounded-lg bg-accent/10 border-2 border-dashed border-accent/40 p-2 flex items-start gap-1 transition-opacity duration-150 motion-reduce:transition-none"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-ink truncate">{slot.recipeName}</p>
          <p className="text-xs text-muted mt-0.5">
            {t('slot.suggestedBy', { name: slot.suggestedByMemberName })}
          </p>
        </div>
        {userRole === 'Planner' && onApprove && onDismiss && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onApprove()
              }}
              aria-label={t('suggestion.approveAriaLabel', { name: slot.recipeName })}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-accent hover:text-green-600 hover:bg-surface-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <Check className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onDismiss()
              }}
              aria-label={t('suggestion.dismissAriaLabel', { name: slot.recipeName })}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-muted hover:text-danger hover:bg-surface-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </>
        )}
      </div>
    )
  }

  // Confirmed filled slot
  const portions = Math.round((slot.servingSize ?? 1) * slot.servingMultiplier)

  return (
    <div className={cn(
      'w-full min-h-[44px] rounded-lg bg-surface border border-line p-2 flex items-start gap-1',
      'active:scale-[0.96] motion-safe:transition-transform',
      isToday && 'border-l-4 border-l-accent',
    )}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => { haptic('light'); onTap() }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onTap()
          }
        }}
        aria-label={t('slot.filledSlotAriaLabel', { name: slot.recipeName, day: dayName })}
        className="flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
      >
        <p className="text-sm font-semibold text-ink truncate">{slot.recipeName}</p>
        <p className="text-xs text-muted mt-0.5">{t('slot.portionsLabel', { count: portions })}</p>
      </div>
      {onEditServing && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onEditServing()
          }}
          aria-label={t('slot.editServingAriaLabel', { name: slot.recipeName, count: portions })}
          className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-muted hover:text-accent hover:bg-surface-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          aria-label={t('slot.removeAriaLabel', { name: slot.recipeName, day: dayName })}
          className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-muted hover:text-danger hover:bg-surface-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}
